import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { Button } from "@/components/ui/Button";
import { Dialog } from "@/components/ui/Dialog";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { ApiError, apiFetch } from "@/lib/api";
import type { DocumentResource, Page, ReferenceListItemResource } from "@/lib/api-types";
import { labelFor } from "@/lib/field-labels";

/**
 * `EquipmentRecord.tsx`'s own "Add document" action — mirrors `routes/
 * properties/AddDocumentDialog.tsx`'s identical shape exactly (Story 7.2's
 * own upload-and-attach-in-one-call contract), parameterized for
 * `entity_type="equipment"` instead of `"property"`. R-E6 ("Attach manuals
 * (PDF) and photos") and the data-plate/unit photos R-P7's own Location
 * field group names both go through this one real mechanism — never a
 * bespoke equipment-only upload path.
 */
export function AddEquipmentDocumentDialog({
  open,
  onOpenChange,
  equipmentId,
  onUploaded,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  equipmentId: string;
  onUploaded: (document: DocumentResource) => void;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [category, setCategory] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);

  const categoriesQuery = useQuery({
    queryKey: ["reference-list-items", "document_category"],
    queryFn: () => apiFetch<Page<ReferenceListItemResource>>("/reference-list-items?list_key=document_category"),
  });
  const categoryOptions = categoriesQuery.data?.data ?? [];

  useEffect(() => {
    if (!open) {
      setFile(null);
      setCategory("");
      setError(undefined);
    }
  }, [open]);

  const canSubmit = file !== null;

  async function handleSubmit() {
    if (file === null) {
      return;
    }
    setIsSubmitting(true);
    setError(undefined);
    try {
      const headers: Record<string, string> = {
        "X-PropDesk-Filename": encodeURIComponent(file.name),
        "X-PropDesk-Entity-Type": "equipment",
        "X-PropDesk-Entity-Id": equipmentId,
      };
      if (category !== "") {
        headers["X-PropDesk-Category"] = category;
      }
      const uploaded = await apiFetch<DocumentResource>("/documents", {
        method: "POST",
        headers,
        body: file,
      });
      onOpenChange(false);
      onUploaded(uploaded);
    } catch (submitError) {
      setError(
        submitError instanceof ApiError ? submitError.message : "Something went wrong. Please try again in a moment.",
      );
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange} title="Add a document">
      <form
        className="mt-4 flex flex-col gap-4"
        onSubmit={(event) => {
          event.preventDefault();
          void handleSubmit();
        }}
      >
        <label className="flex flex-col gap-1">
          <span className="text-label font-medium text-ink-secondary">File</span>
          <Input
            type="file"
            autoFocus
            accept=".pdf,.jpg,.jpeg,.png,.heic,application/pdf,image/jpeg,image/png,image/heic"
            onChange={(event) => setFile(event.target.files?.[0] ?? null)}
          />
        </label>

        <label className="flex flex-col gap-1">
          <span className="text-label font-medium text-ink-secondary">{labelFor("category")}</span>
          <Select value={category} onChange={(event) => setCategory(event.target.value)}>
            <option value="">Uncategorised</option>
            {categoryOptions.map((option) => (
              <option key={option.id} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
        </label>

        {error ? (
          <p role="alert" className="text-meta text-danger">
            {error}
          </p>
        ) : null}

        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="submit" disabled={!canSubmit || isSubmitting}>
            {isSubmitting ? "Uploading…" : "Add document"}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
